/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    ContentScreenDetails,
    ContentScreenType,
    Detail,
    EpisodeScreenDetails,
    GameScreenDetails,
    MovieScreenDetails,
} from 'client-code/actions';

import { Locale, LocaleKeys } from '../../core/locale';
import { ScreenController } from '../../core/navigation/ScreenController';
import { SharedPrefConstants, SharedPreferences } from '../../core/storage';
import { DateTime } from '../../core/utils';
import { ProgramScreenConstants } from '../../store/programscreen/ProgramScreenConstants';
import { THorizontalActionStripLayout } from './BiaxialActionWidget/THorizontalActionStripItem';
import { ContentScreenPresentationId } from './ContentScreenPresentationId';

export class ProgramScreenHelper {
    /**
     * This method dynamically constructs the title and title suffix based on the type of content
     * screen.
     *
     * @param {ContentScreenDetails} contentScreenDetails - The details of the content screen,
     * including the title and type.
     * @returns {Object} An object containing:
     *   - `title` (string): The base title of the content.
     *   - `titleSuffix` (string): The dynamically generated suffix based on the content type.
     */
    public static generateScreenTitle(contentScreenDetails: ContentScreenDetails): {
        title: string;
        titleSuffix: string;
    } {
        const { title, contentScreenType } = contentScreenDetails;
        let modifiedTitle = title;
        let titleSuffix = '';
        let isGame = false;
        switch (contentScreenType) {
            case ContentScreenType.EpisodeScreenDetails: {
                const { episodeNumber, firstAiredDate, seasonNumber } =
                    contentScreenDetails as EpisodeScreenDetails;
                const episodeText: string =
                    episodeNumber && episodeNumber > 0 ? `E${episodeNumber}` : '';
                const seasonText: string =
                    seasonNumber && seasonNumber > 0 ? `S${seasonNumber} ` : '';
                const seasonEpisodeText = (seasonText + episodeText).length
                    ? `${seasonText}${episodeText} `
                    : '';
                if (seasonEpisodeText.length) {
                    titleSuffix = (seasonText + episodeText).length
                        ? `${seasonText}${episodeText} `
                        : '';
                } else if (firstAiredDate) {
                    titleSuffix = `${new DateTime(firstAiredDate).strftime('%M/%D/%y')}`;
                } else {
                    titleSuffix = `${Locale.getString(
                        LocaleKeys.EPISODE_SCREEN_TITLE_ULTIMATE_FALLBACK,
                    )}`;
                }
                break;
            }
            case ContentScreenType.MovieScreenDetails: {
                const { movieYear } = contentScreenDetails as MovieScreenDetails;
                if (movieYear) titleSuffix = `(${movieYear})`;
                break;
            }
            case ContentScreenType.GameScreenDetails: {
                const { gameTitle } = contentScreenDetails as GameScreenDetails;
                if (gameTitle) modifiedTitle += ` - ${gameTitle}`;
                isGame = true;
                break;
            }
            default:
        }

        const titleSharedPref = new SharedPreferences(
            SharedPrefConstants.PROGRAM_SCREEN_PREF_KEY,
        ).edit();
        titleSharedPref.putJson(SharedPrefConstants.PROGRAM_SCREEN_TITLE, {
            title,
            isGame,
        } as unknown as JSON);
        titleSharedPref.apply();

        return { title: modifiedTitle, titleSuffix };
    }

    public static generateListViewTitle(filter: THorizontalActionStripLayout): string {
        const titleSharedPref = new SharedPreferences(SharedPrefConstants.PROGRAM_SCREEN_PREF_KEY);
        const titleJson = titleSharedPref.getJson(
            SharedPrefConstants.PROGRAM_SCREEN_TITLE,
        ) as unknown as {
            title: string;
            isGame: boolean;
        };

        let prefix = '';
        if (filter === ProgramScreenConstants.ALL_UPCOMING_EPISODES)
            prefix = titleJson.isGame
                ? LocaleKeys.PS_OPTION_UPCOMING_EPISODES_DETAIL_GAME
                : LocaleKeys.PS_OPTION_UPCOMING_EPISODES_DETAIL;
        else if (filter === ProgramScreenConstants.UPCOMING_AIRING)
            prefix = LocaleKeys.PS_OPTION_UPCOMING_AIRING_DETAIL;
        else if (filter === ProgramScreenConstants.ALL_EPISODES)
            prefix = LocaleKeys.PS_OPTION_SEASON_OR_YEAR_DETAIL;
        else if (filter === ProgramScreenConstants.ON_DEMAND_EPISODES)
            prefix = LocaleKeys.PS_OPTION_ON_DEMAND_EPISODES_DETAIL;

        return [prefix && Locale.getString(prefix), titleJson.title].filter(Boolean).join(': ');
    }

    /**
     * Calculates the initial vertical index for a content screen based on the specified
     * presentation ID.
     *
     * @param {ContentScreenPresentationId | undefined} targetInitialProgramScreenOption - Target
     *        presentation ID to find in the action list
     * @param {Detail[]} actionList - Array of action details to search through
     * @returns {number} Index of the matching presentation ID, or -1 if not found or inputs are
     *          invalid
     */
    public static calculateInitialVerticalIndex(
        targetInitialProgramScreenOption: ContentScreenPresentationId | undefined,
        actionList: Detail[],
    ) {
        if (!actionList || actionList.length === 0 || !targetInitialProgramScreenOption) {
            return -1;
        }
        const index = actionList.findIndex((action) => {
            return action.presentation?.presentationId === targetInitialProgramScreenOption;
        });

        return index;
    }

    public static resetTargetInitialProgramScreenOption() {
        const currScreen = ScreenController.getInstance().getScreen();
        const currScreenProperties = ScreenController.getInstance().getScreen().getProperties()!;
        currScreen.setProperties({
            ...currScreenProperties,
            targetInitialProgramScreenOption: undefined,
        });
    }
}
